import React, { useState } from "react"
import { Button } from "@aix/ui"
import { Upload, FileCode, X } from "lucide-react"
import toast from "react-hot-toast"
import { trpc } from "../../../lib/trpc"

interface ImportModalProps {
  isOpen: boolean
  onClose: () => void
  selectedSystemId: string | null
}

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, selectedSystemId }) => {
  const [sqlContent, setSqlContent] = useState("")
  const [importPreview, setImportPreview] = useState<any>(null)

  const utils = trpc.useUtils()

  // Mutations
  const importSQL = trpc.databrain.importFromSQL.useMutation({
    onSuccess: (data) => {
      setImportPreview(data)
      if (data.proposedTables.length === 0) {
        toast.error("No tables found in SQL")
      } else {
        toast.success(data.message)
      }
    },
    onError: (error) => {
      toast.error(`Failed to parse SQL: ${error.message}`)
    },
  })

  const commitImport = trpc.databrain.commitImport.useMutation({
    onSuccess: (data) => {
      toast.success(data.message)
      setSqlContent("")
      setImportPreview(null)
      // Invalidate graph query to refresh the data
      void utils.databrain.getGraph.invalidate()
      onClose()
    },
    onError: (error) => {
      toast.error(`Failed to import: ${error.message}`)
    },
  })

  const handleParseSQL = () => {
    if (!selectedSystemId) {
      toast.error("Please select a system first")
      return
    }
    if (!sqlContent.trim()) {
      toast.error("Please paste SQL content")
      return
    }

    importSQL.mutate({
      systemId: selectedSystemId,
      sqlContent,
    })
  }

  const handleCommitImport = () => {
    if (!selectedSystemId || !importPreview) return

    commitImport.mutate({
      systemId: selectedSystemId,
      tables: importPreview.proposedTables,
      columns: importPreview.proposedColumns,
    })
  }

  const handleClose = () => {
    setSqlContent("")
    setImportPreview(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background">
            <div className="flex items-center gap-2">
              <FileCode className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Import Schema</h2>
            </div>
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Import Section */}
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Paste any of the following: SQL DDL (CREATE TABLE), Zod schemas, TypeScript interfaces, or JSON
                examples. The AI parser will automatically extract tables, columns, and relationships.
              </p>

              <textarea
                value={sqlContent}
                onChange={(e) => setSqlContent(e.target.value)}
                placeholder={`SQL DDL:
CREATE TABLE users (
  id UUID PRIMARY KEY,
  email VARCHAR(255) NOT NULL
);

Zod Schema:
const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email()
});

TypeScript:
interface User {
  id: string;
  email: string;
}

JSON Example:
{
  "id": "123e4567-e89b",
  "email": "user@example.com"
}`}
                className="w-full h-64 px-3 py-2 border border-border rounded-md bg-background text-foreground font-mono text-sm"
                disabled={!selectedSystemId}
              />

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleParseSQL}
                  loading={importSQL.isLoading}
                  disabled={!selectedSystemId || !sqlContent}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Parse Schema
                </Button>
                {importPreview && (
                  <Button onClick={handleCommitImport} loading={commitImport.isLoading} variant="default">
                    Commit Import ({importPreview.proposedTables.length} tables)
                  </Button>
                )}
              </div>
            </div>

            {/* Import Preview */}
            {importPreview && (
              <div className="bg-secondary border border-border rounded-lg p-6">
                <h3 className="text-sm font-medium text-foreground mb-4">Import Preview</h3>

                <div className="space-y-4">
                  {importPreview.proposedTables.map((table: any, idx: number) => {
                    const tableCols = importPreview.proposedColumns.filter((c: any) => c.tableName === table.name)
                    return (
                      <div key={idx} className="bg-background border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-foreground">
                            {table.schema}.{table.name}
                          </h4>
                          <span className="text-xs text-muted-foreground">{tableCols.length} columns</span>
                        </div>
                        <div className="space-y-1">
                          {tableCols.map((col: any, colIdx: number) => (
                            <div key={colIdx} className="flex items-center gap-2 text-sm">
                              <span className="font-mono text-foreground">{col.name}</span>
                              <span className="text-muted-foreground">{col.dataType}</span>
                              {col.isPrimaryKey && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-primary/10 text-primary">PK</span>
                              )}
                              {!col.isNullable && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                                  NOT NULL
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ImportModal
